import { useEffect, useState } from 'react';
import type { Order } from './types';

export type ProcessingLock = Order['processingLock'];

/**
 * Whether a processingLock is currently in effect: present and not past its
 * `expiresAt`. A lock with no `expiresAt` is treated as indefinite. Single
 * source of truth for "is this order locked" — used by both the Orders list
 * (via `useIsLockActive`) and can be reused wherever a point-in-time check
 * (no timer) is enough.
 */
export function isLockActive(lock: ProcessingLock, now: number = Date.now()): boolean {
  if (!lock) return false;
  if (!lock.expiresAt) return true;
  return new Date(lock.expiresAt).getTime() > now;
}

/**
 * Reactive lock-active flag. Schedules a re-check exactly at `expiresAt` so a
 * row self-heals to "unlocked" even if the `orderProcessingCompleted` SSE
 * event is missed — mirroring the fallback already used on the OrderDetails
 * page (`features/orders/OrderDetails/index.tsx`).
 */
// Browsers store timer delays as 32-bit signed ints; anything larger fires
// immediately, which would prematurely "unlock" a far-future lock.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

export function useIsLockActive(lock: ProcessingLock): boolean {
  const [active, setActive] = useState(() => isLockActive(lock));

  useEffect(() => {
    setActive(isLockActive(lock));
    if (!lock?.expiresAt) return;
    let id: ReturnType<typeof setTimeout> | undefined;
    const schedule = (): void => {
      const ms = new Date(lock.expiresAt!).getTime() - Date.now();
      if (ms <= 0) {
        setActive(false);
        return;
      }
      // Clamped waits re-check on wake instead of unlocking early.
      id = setTimeout(() => {
        if (isLockActive(lock)) schedule();
        else setActive(false);
      }, Math.min(ms, MAX_TIMEOUT_MS));
    };
    schedule();
    return () => clearTimeout(id);
  }, [lock]);

  return active;
}
